import { useSyncExternalStore } from "react";
import {
  getLoadVersion,
  isSceneReady,
  subscribeLoadProgress,
  summarizeLoad,
} from "../three/loadProgress";
import { t } from "../lib/uiStrings";

const mb = (bytes: number) => (bytes / 1e6).toFixed(1);

/**
 * Horizontal loading bar on the intro card. `keys` are the spec URLs the room
 * downloads (glTF room + props, or a panorama); an empty list means a room
 * built in code, which is ready as soon as the 3D canvas exists. `ready`
 * overrides the download state for glTF rooms, which are only usable after
 * parsing and shader compilation (GltfRoom's `cf-gltf-ready`).
 */
export function IntroLoadBar({ keys, ready: readyProp }: { keys: string[]; ready?: boolean }) {
  useSyncExternalStore(subscribeLoadProgress, getLoadVersion);
  const sum = summarizeLoad(keys);
  const ready = keys.length === 0 ? isSceneReady() : readyProp ?? sum.allDone;

  let pct: number | null = null;
  if (ready) pct = 100;
  else if (sum.total > 0) pct = Math.min(96, 3 + (sum.loaded / sum.total) * 92);
  const indeterminate = pct === null;
  const downloading = !ready && sum.total > 0 && sum.loaded < sum.total;

  const label = ready
    ? t("intro.roomReady")
    : downloading
      ? t("intro.loadingRoom")
      : t("intro.preparingRoom");
  const size =
    sum.total > 0 ? (ready ? `${mb(sum.total)} MB` : `${mb(sum.loaded)} / ${mb(sum.total)} MB`) : "";

  return (
    <div
      className={`intro-loadbar${ready ? " is-ready" : ""}${indeterminate ? " is-indeterminate" : ""}`}
      role="progressbar"
      aria-label={t("intro.loadingRoom")}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct === null ? undefined : Math.round(pct)}
      aria-valuetext={label}
    >
      <div className="intro-loadbar__head">
        <span className="intro-loadbar__label">
          {ready && (
            <svg className="intro-loadbar__check" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          {label}
          {!ready && <span className="intro-loadbar__dots" aria-hidden="true" />}
        </span>
        <span className="intro-loadbar__value">
          {pct !== null && <b>{Math.round(pct)}%</b>}
          {size && <span className="intro-loadbar__size">{size}</span>}
        </span>
      </div>
      <div className="intro-loadbar__track" aria-hidden="true">
        <div className="intro-loadbar__fill" style={indeterminate ? undefined : { width: `${pct}%` }} />
      </div>
    </div>
  );
}
